import React, { useMemo, useCallback, useRef, useState, useEffect } from "react";
import Select, { ActionMeta, SingleValue, StylesConfig } from "react-select";
import { useTranslation } from "react-i18next";
import useVehiclesState from "../../../model/useVehiclesState";
import useBuyFormStore from "stores/useBuyFormStore";
import styles from "./VehiclePicker.module.scss";
import SelectOptionVehicle from "../../SelectOption/SelectOptionVehicle";
import SelectSingleValueVehicle from "../../SelectOption/SelectSingleValueVehicle";
import { colourStyles } from "../../form/Select/Select";
import { IVehiclePickerProps, IVehicleOption, IVehicle } from "types/vehiclePicker.type";

const CUSTOM_OPTION_VALUE = "custom";

const VehiclePicker: React.FC<IVehiclePickerProps> = ({
    name,
    defaultValue = "",
    placeholder,
    className,
    isDisabled = false,
}) => {
    const { t } = useTranslation();
    const { vehicles } = useVehiclesState();
    const customInputRef = useRef<HTMLInputElement>(null);

    const { licencePlate, setLicencePlate } = useBuyFormStore();

    const [isCustom, setIsCustom] = useState(false);

    const initialValues = useMemo(() => {
        if (defaultValue && !licencePlate) {
            const isInVehicles = vehicles.some((v) => v.spz === defaultValue);

            return {
                licencePlate: defaultValue,
                isCustom: !isInVehicles,
            };
        }

        if (licencePlate) {
            const isInVehicles = vehicles.some((v) => v.spz === licencePlate);

            return {
                licencePlate,
                isCustom: !isInVehicles,
            };
        }

        return {
            licencePlate: "",
            isCustom: true,
        };
    }, [vehicles]);

    useEffect(() => {
        setLicencePlate(initialValues.licencePlate);
        setIsCustom(initialValues.isCustom);
    }, [initialValues, vehicles]);

    const options = useMemo((): IVehicleOption[] => {
        const vehicleOptions = vehicles.map((vehicle: IVehicle) => ({
            value: vehicle.spz,
            label: vehicle.name,
        }));

        vehicleOptions.push({
            value: CUSTOM_OPTION_VALUE,
            label: t("VehiclePickerComponent.newVehicle"),
        });

        return vehicleOptions;
    }, [vehicles, t]);

    const selectedOption = useMemo((): IVehicleOption | null => {
        if (!licencePlate) return null;

        const existingVehicle = vehicles.find((vehicle: IVehicle) => vehicle.spz === licencePlate);

        if (existingVehicle) {
            return {
                value: existingVehicle.spz,
                label: existingVehicle.name,
            };
        }

        if (licencePlate && isCustom) {
            return {
                value: CUSTOM_OPTION_VALUE,
                label: t("VehiclePickerComponent.newVehicle"),
                isCustom: true,
            };
        }

        return null;
    }, [licencePlate, vehicles, t, isCustom]);

    const customStyles = useMemo(
        (): StylesConfig => ({
            ...colourStyles,
            control: (provided: any, state: any) => ({
                ...colourStyles.control(provided, state),
                backgroundColor: isCustom ? "#F3F3F5" : state.isFocused ? "#fff" : "#F3F3F5",
            }),
        }),
        [isCustom]
    );

    const handleSelectChange = useCallback(
        (newValue: SingleValue<IVehicleOption>, _actionMeta: ActionMeta<IVehicleOption>) => {
            if (!newValue) {
                setLicencePlate("");
                setIsCustom(true);
                return;
            }

            if (newValue.value !== CUSTOM_OPTION_VALUE) {
                setLicencePlate(newValue.value);
                setIsCustom(false);
            } else {
                setLicencePlate("");
                setIsCustom(true);

                requestAnimationFrame(() => {
                    customInputRef.current?.focus();
                });
            }
        },
        [setLicencePlate]
    );

    const handleCustomInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value.toUpperCase();
            setLicencePlate(value);
        },
        [setLicencePlate]
    );

    const handleCustomInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Escape") {
                const lastValidVehicle = vehicles.find((vehicle: IVehicle) => vehicle.spz === defaultValue);

                if (lastValidVehicle) {
                    setLicencePlate(lastValidVehicle.spz);
                    setIsCustom(false);
                } else {
                    setLicencePlate("");
                    setIsCustom(true);
                }
                customInputRef.current?.blur();
            }
        },
        [vehicles, defaultValue, setLicencePlate]
    );

    const formatOptionLabel = useCallback((option: IVehicleOption) => {
        if (option.isCustom || option.value === CUSTOM_OPTION_VALUE) {
            return option.label;
        }
        return `${option.label} - ${option.value.toUpperCase()}`;
    }, []);

    return (
        <div className={`${styles._container} ${className || ""}`}>
            <Select<IVehicleOption>
                name={name}
                options={options}
                value={selectedOption}
                onChange={handleSelectChange}
                styles={customStyles}
                isSearchable={false}
                isDisabled={isDisabled}
                placeholder={placeholder || t("buy.carSelectPlaceholder")}
                components={{
                    IndicatorSeparator: () => null,
                    Option: SelectOptionVehicle,
                    SingleValue: SelectSingleValueVehicle,
                }}
                getOptionLabel={formatOptionLabel}
            />
            {isCustom && (
                <input
                    ref={customInputRef}
                    className={styles.customSpz}
                    value={licencePlate}
                    maxLength={10}
                    onChange={handleCustomInputChange}
                    onKeyDown={handleCustomInputKeyDown}
                    disabled={isDisabled}
                    placeholder={t("VehiclePickerComponent.licencePlate")}
                    autoComplete="off"
                    spellCheck={false}
                    aria-label={t("VehiclePickerComponent.licencePlate")}
                />
            )}
        </div>
    );
};

export default React.memo(VehiclePicker);
